import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import Prism from './Prism';

function RichText({ children }) {
  const text = typeof children === 'string' ? children : String(children);
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
          return (
            <strong key={i} className="font-semibold text-zinc-50">
              {part.slice(2, -2)}
            </strong>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function TypingIndicator() {
  return (
    <div
      className="mr-auto flex items-center gap-2 rounded-2xl border border-zinc-600/50 bg-zinc-900/85 px-4 py-3 shadow-sm backdrop-blur-md"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500">V</span>
      <span className="flex gap-1">
        {[0, 1, 2].map((n) => (
          <span
            key={n}
            className="h-1.5 w-1.5 rounded-full bg-primary motion-reduce:animate-none motion-reduce:opacity-80 animate-v-typing-dot"
            style={{ animationDelay: `${n * 140}ms` }}
          />
        ))}
      </span>
    </div>
  );
}

function ChatBubble({ role, children, staggerMs = 0 }) {
  const isUser = role === 'user';
  return (
    <div
      className={cn(
        'max-w-[92%] motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:transform-none animate-v-bubble-in rounded-2xl px-4 py-3 text-[13px] leading-relaxed',
        isUser
          ? 'ml-auto border border-blue-400/35 bg-blue-600/95 text-white shadow-md shadow-blue-950/50 backdrop-blur-sm'
          : 'mr-auto border border-zinc-600/60 bg-zinc-900/88 text-zinc-100 shadow-sm backdrop-blur-md'
      )}
      style={{ animationDelay: `${staggerMs}ms` }}
    >
      <div className={cn('whitespace-pre-wrap [word-break:break-word]', isUser ? 'text-white' : 'text-zinc-200')}>
        {typeof children === 'string' ? <RichText>{children}</RichText> : children}
      </div>
    </div>
  );
}

export function VAssistant() {
  const titleId = useId();
  const { context } = usePilotContext();
  const llmOn = !!context?.v_assist_enabled;
  const staticGuideOn = context?.v_nav_static_enabled === true;
  const canUseChat = llmOn || staticGuideOn;
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState(() => [
    {
      role: 'assistant',
      content:
        "Hi — I'm V. Ask how to use AERTA, what a section is for, or where to go next. I only know the product map (not your live findings).",
    },
  ]);
  const bottomRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, sending]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    if (!canUseChat) {
      setMessages((m) => [
        ...m,
        { role: 'user', content: text },
        {
          role: 'assistant',
          content:
            'This build cannot reach the navigation guide API. Check that you are signed in and the app can call the API (REACT_APP_BACKEND_URL in production).',
        },
      ]);
      return;
    }
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await api.post('/api/v/chat', { message: text });
      const answer = res?.answer || 'No answer returned.';
      setMessages((m) => [...m, { role: 'assistant', content: answer }]);
    } catch (e) {
      const msg = e?.message || 'Request failed.';
      setMessages((m) => [...m, { role: 'assistant', content: `Could not reach V: ${msg}` }]);
    } finally {
      setSending(false);
    }
  }, [canUseChat, input, sending]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'group fixed bottom-5 right-5 z-[100] flex h-14 w-14 items-center justify-center rounded-full',
          'border border-blue-500/40 bg-blue-600 text-white',
          'motion-reduce:shadow-lg motion-reduce:animate-none animate-v-fab-idle',
          'shadow-lg shadow-blue-950/50',
          'transition-[transform,box-shadow] duration-200 ease-out hover:scale-105 hover:shadow-xl hover:shadow-blue-900/40',
          'active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950',
          open && 'pointer-events-none opacity-0'
        )}
        aria-label="Open V assistant"
        title="V — navigation help"
        data-testid="v-assistant-fab"
      >
        <span className="font-heading text-lg font-black tracking-tight">V</span>
      </button>

      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[35] animate-in fade-in duration-200 bg-black/50 backdrop-blur-[2px] motion-reduce:animate-none"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
          />
          <div
            className={cn(
              // Must stay `fixed` — do not add `relative` here; it overrides `fixed` via tailwind-merge and pins the panel to the top of the layout.
              'fixed bottom-5 right-5 z-[100] flex w-[min(100vw-1.5rem,26rem)] flex-col overflow-hidden rounded-2xl',
              'motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:transform-none animate-v-panel-in',
              'border border-zinc-600/50 shadow-2xl shadow-black/60 ring-1 ring-white/[0.08]'
            )}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-label="V assistant"
            data-testid="v-assistant-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-2xl">
              {reducedMotion ? (
                <div
                  className="h-full w-full bg-gradient-to-br from-zinc-900 via-[#0d0d12] to-zinc-950"
                  aria-hidden
                />
              ) : (
                <Prism
                  animationType="rotate"
                  timeScale={0.5}
                  height={3.5}
                  baseWidth={5.5}
                  scale={3.6}
                  hueShift={0}
                  colorFrequency={1}
                  noise={0}
                  glow={1}
                  suspendWhenOffscreen
                />
              )}
            </div>
            {/* Readability scrim — keeps Prism visible but text legible */}
            <div
              className="pointer-events-none absolute inset-0 z-[1] rounded-2xl bg-gradient-to-b from-zinc-950/78 via-zinc-950/62 to-zinc-950/82"
              aria-hidden
            />

            <header className="relative z-10 flex shrink-0 items-start justify-between gap-3 border-b border-zinc-800/80 bg-zinc-950/55 px-4 pb-3 pt-3.5 backdrop-blur-md">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-zinc-950 font-heading text-xl font-black tracking-tight text-white ring-1 ring-zinc-700"
                  aria-hidden
                >
                  V
                </div>
                <div className="min-w-0">
                  <p id={titleId} className="font-heading text-sm font-bold tracking-tight text-zinc-100">
                    V
                    <span className="ml-1.5 font-normal text-zinc-500">· guide</span>
                  </p>
                  <p className="mt-0.5 text-xs text-zinc-500">Product navigation (not live tenant data)</p>
                  <p className="mt-2 inline-flex max-w-full items-center gap-1.5 truncate rounded-lg bg-zinc-950/80 px-2 py-1 font-mono text-[11px] text-zinc-400 ring-1 ring-zinc-800">
                    <MessageCircle className="h-3 w-3 shrink-0 text-zinc-500" aria-hidden />
                    {location.pathname}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-lg text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </header>

            {!llmOn ? (
              <p className="relative z-10 border-b border-sky-900/40 bg-sky-950/35 px-4 py-2.5 text-xs leading-snug text-sky-100/95 backdrop-blur-sm">
                {staticGuideOn
                  ? "Answering from the server’s curated guide (no LLM). Enable AERTA_LLM_* on the API for full AI-style replies."
                  : 'Loading feature flags from the API…'}
              </p>
            ) : null}
            {llmOn && context?.llm_reconnecting ? (
              <p className="relative z-10 border-b border-amber-700/50 bg-amber-950/50 px-4 py-2.5 text-xs leading-snug text-amber-100/95 backdrop-blur-sm">
                LLM reconnecting… — the API is retrying the model provider; chat may be slow until healthy.
              </p>
            ) : null}
            {llmOn && context?.llm_operational === false ? (
              <p className="relative z-10 border-b border-red-900/45 bg-red-950/40 px-4 py-2.5 text-xs leading-snug text-red-100/95 backdrop-blur-sm">
                LLM health check failed — verify AERTA_LLM_* on the API. Chat may return errors until the provider is
                reachable.
              </p>
            ) : null}

            <div className="relative z-10 max-h-[min(52vh,440px)] min-h-[200px] overflow-y-auto bg-zinc-950/20 px-4 py-4 custom-scrollbar">
              <div className="flex flex-col gap-4">
                {messages.map((msg, i) => (
                  <ChatBubble key={`${i}-${msg.role}`} role={msg.role} staggerMs={Math.min(i * 40, 360)}>
                    {msg.content}
                  </ChatBubble>
                ))}
                {sending ? <TypingIndicator /> : null}
                <div ref={bottomRef} />
              </div>
            </div>

            <footer className="relative z-10 border-t border-zinc-800/80 bg-zinc-950/45 p-3 backdrop-blur-md">
              <div className="flex gap-2">
                <input
                  className={cn(
                    'min-h-[46px] flex-1 rounded-xl border border-zinc-600/50 bg-zinc-950/70 px-4 py-2.5 text-sm text-zinc-100 outline-none',
                    'placeholder:text-zinc-500',
                    'backdrop-blur-sm transition-shadow duration-150',
                    'focus-visible:border-zinc-500 focus-visible:ring-2 focus-visible:ring-blue-500/30'
                  )}
                  placeholder="Ask about a section or how to use AERTA…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                />
                <Button
                  type="button"
                  size="icon"
                  className={cn(
                    'h-[46px] w-[46px] shrink-0 rounded-xl border border-blue-500/40 bg-blue-600 text-white',
                    'shadow-md shadow-blue-950/40 transition-transform duration-150 hover:bg-blue-500 active:scale-95',
                    'disabled:opacity-45 disabled:hover:bg-blue-600 disabled:active:scale-100'
                  )}
                  disabled={sending}
                  onClick={send}
                  aria-label="Send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </footer>
          </div>
        </>
      ) : null}
    </>
  );
}
